import { inject, Injectable } from '@angular/core';
import { collection, doc, Firestore, getDoc, onSnapshot } from '@angular/fire/firestore';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { ModalController } from '@ionic/angular';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { catchError, defaultIfEmpty, distinctUntilChanged, filter, map, retry, startWith, switchMap, take, timeout } from 'rxjs/operators';
import { combineLatest, concat, defer, firstValueFrom, fromEvent, Observable, of, Subscription, throwError, timer } from 'rxjs';
import { CommonService } from 'src/app/services/common/common.service';
import { PaymentService } from 'src/app/services/payment.service';
import { BasePaymentService } from './base-payment-service';
import { LoadingService } from '@core/loading.service';
import { PaidPurchasePreparationComponent } from './paid-purchase-preparation.component';

export interface CallInt2Context {
    Prosub_id: string;
    TP1: string;
    Tp2: string;
    TP3: string;
    T_ID: string;
    upsellID: string;
    timerOn: boolean;
    spinnerAnimation: boolean;
    isPaymentDone: boolean;
    addingCard: boolean;
    addNewCardInProgress: boolean;
    _syndicateId?: string;
    isCurrent?: () => boolean;
    getCheckCardDetailId: () => { id?: string; [key: string]: unknown };
    onStateChange: (patch: Partial<CallInt2Context>) => void;
}

/** Only the persisted boolean is a profile decision; missing data stays unknown. */
export function profileCompletionState(user: Data | null | undefined): boolean | null {
    return typeof user?.minimal_required === "boolean" ? user.minimal_required : null;
}

export class PostPaymentPreparationError extends Error {
    readonly code = "post_payment_preparing";
    constructor(readonly reason: "profile_pending" | "tickets_pending") {
        super(reason === "profile_pending" ? "Profile status is still loading" : "Purchased tickets are still preparing");
        this.name = "PostPaymentPreparationError";
    }
}

@Injectable()
export class PaymentNavigationService {
    private modalController = inject(ModalController);
    private router = inject(Router);
    private actRoute = inject(ActivatedRoute);
    private basePaymentService = inject(BasePaymentService);
    private authService = inject(AuthService);
    private loadingService = inject(LoadingService);
    private paymentService = inject(PaymentService);
    private commonService = inject(CommonService);

    private preparationModal?: HTMLIonModalElement;
    private preparationOpening?: Promise<void>;
    private preparationEpoch = 0;
    private preparationAuthUnsubscribe?: () => void;

    constructor(protected firestore: Firestore) { }

    /** The page calls this on leave/destroy; only this service's overlay is removed. */
    clearPreparation(): void {
        this.preparationEpoch = (this.preparationEpoch ?? 0) + 1;
        this.preparationOpening = undefined;
        this.preparationAuthUnsubscribe?.();
        this.preparationAuthUnsubscribe = undefined;
        const modal = this.preparationModal;
        this.preparationModal = undefined;
        if (modal) this.removePreparationModal(modal);
    }

    private removePreparationModal(modal: HTMLIonModalElement): void {
        try { void modal.dismiss().catch(() => undefined); } catch { /* Scoped removal is still safe. */ }
        modal.remove?.();
    }

    private async showPreparation(ctx: CallInt2Context): Promise<void> {
        this.assertCurrentNavigation(ctx.isCurrent);
        // Never describe an unconfirmed payment as received.
        if (ctx.isPaymentDone !== true || this.preparationModal) return;
        if (this.preparationOpening) return this.preparationOpening;
        const epoch = this.preparationEpoch ?? 0;
        const ownerUserId = getAuth().currentUser?.uid;
        const isCurrent = () => epoch === (this.preparationEpoch ?? 0) && ctx.isCurrent?.() !== false;
        const opening = (async () => {
            const modal = await this.modalController.create({
                component: PaidPurchasePreparationComponent,
                cssClass: "modal-fullScreen",
                backdropDismiss: false,
            });
            if (!isCurrent()) {
                this.removePreparationModal(modal);
                this.assertCurrentNavigation(() => false);
            }
            this.preparationModal = modal;
            try {
                await modal.present();
                this.assertCurrentNavigation(isCurrent);
                // A preparation view outlives an individual failed navigation
                // attempt, but must not outlive its authenticated purchase owner.
                const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
                    if (!user || user.isAnonymous || user.uid !== ownerUserId) this.clearPreparation();
                }, () => this.clearPreparation());
                if (this.preparationModal === modal) this.preparationAuthUnsubscribe = unsubscribe;
                else unsubscribe();
            } catch (error) {
                if (this.preparationModal === modal) this.preparationModal = undefined;
                this.removePreparationModal(modal);
                throw error;
            }
        })();
        this.preparationOpening = opening;
        try { await opening; } finally {
            if (this.preparationOpening === opening) this.preparationOpening = undefined;
        }
    }

    private async remainPreparing(ctx: CallInt2Context, reason: "profile_pending" | "tickets_pending"): Promise<never> {
        this.assertCurrentNavigation(ctx.isCurrent);
        try { await this.showPreparation(ctx); } catch (error) {
            this.assertCurrentNavigation(ctx.isCurrent);
            this.authService.errorHandlerLogs("Paid preparation view unavailable", error);
        }
        this.assertCurrentNavigation(ctx.isCurrent);
        throw new PostPaymentPreparationError(reason);
    }


    async showTicket(data: Data, fromPostPayment = false, isCurrent?: () => boolean, signal?: AbortSignal): Promise<boolean> {
        this.assertCurrentNavigation(isCurrent);
        const isCheckoutNewCard =
            fromPostPayment && localStorage.getItem("sjFlow") === "addCard";
        const componentProps = fromPostPayment
            ? {
                syndicate: {
                    ...data,
                    syndicate_id: null,
                    product: [],
                },
                isSalesJourney: true,
                isAddCardCashPurchase: isCheckoutNewCard,
            }
            : { syndicate: data };

        const ticketsModule = await import(
            'src/app/pages/syndicate/syndicate-tickets/syndicate-tickets.component'
        ).catch((err) => {
            this.authService.errorHandlerLogs("showTicket: failed to load syndicate-tickets chunk", err);
            return null;
        });
        this.assertCurrentNavigation(isCurrent);
        const SyndicateTicketsComponent = ticketsModule?.SyndicateTicketsComponent;
        if (!SyndicateTicketsComponent) {
            this.authService.errorHandlerLogs("showTicket: SyndicateTicketsComponent unavailable", null);
            if (!fromPostPayment) this.basePaymentService.showToast("1014", "Something went wrong!", "Error");
            return false;
        }
        const syndicateTickets = await this.modalController.create({
            component: SyndicateTicketsComponent,
            componentProps,
            cssClass: "modal-fullScreen",
        });

        let modalDiscarded = false;
        const discardExpiredModal = () => {
            if (modalDiscarded) return;
            modalDiscarded = true;
            // Do not await a stuck Ionic transition before exposing the fallback.
            try { void syndicateTickets.dismiss().catch(() => undefined); } catch { /* Scoped DOM removal still runs. */ }
            syndicateTickets.remove?.();
        };
        if (signal?.aborted) {
            discardExpiredModal();
            this.assertCurrentNavigation(() => false);
        }
        signal?.addEventListener("abort", discardExpiredModal, { once: true });
        try {
            await this.checkTicketOwner(syndicateTickets, isCurrent);
            await syndicateTickets.present();
            if (signal?.aborted) this.assertCurrentNavigation(() => false);
            await this.checkTicketOwner(syndicateTickets, isCurrent);
            return true;
        } catch (error) {
            // A rejected Ionic presentation can leave its created overlay behind.
            // Superseded/aborted owners already clean their own modal above.
            if (!signal?.aborted && isCurrent?.() !== false) discardExpiredModal();
            throw error;
        } finally {
            signal?.removeEventListener("abort", discardExpiredModal);
        }
    }

    private assertCurrentNavigation(isCurrent?: () => boolean): void {
        if (isCurrent?.() === false) {
            throw Object.assign(new Error("Post-payment navigation owner changed"), { code: "post_payment_navigation_superseded" });
        }
    }

    private async checkTicketOwner(tickets: { dismiss?: () => Promise<boolean>; remove?: () => void }, isCurrent?: () => boolean): Promise<void> {
        if (isCurrent?.() !== false) return;
        const error = Object.assign(new Error("Post-payment navigation owner changed"), { code: "post_payment_navigation_superseded" });
        let dismissed = false;
        try { dismissed = await tickets.dismiss?.() === true; } catch { /* Fall back to scoped DOM removal. */ }
        if (!dismissed) tickets.remove?.();
        throw error;
    }

    redirectWithFullPageLoad(route: string, queryParams: Record<string, unknown>): void {
        const params = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
            if (key === "purchase_ID" || key === "purchase_id") {
                return;
            }
            params.set(key, value == null ? "" : String(value));
        });
        const normalizedRoute = route.startsWith("/") ? route : `/${route}`;
        window.location.href = `${normalizedRoute}?${params.toString()}`;
    }

    handleNavigationForOfferCapping(r: Data) {

        const TP1 = localStorage.getItem("TP1");
        const upsell_ID = localStorage.getItem("upsellID");
        const TP2 = localStorage.getItem("TP2");
        const TP3 = localStorage.getItem("TP3");
        const T_ID = localStorage.getItem("T_ID");
        const Other = localStorage.getItem("Other");

        const isOfferCapped =
            r?.offerCapping?.cappingStatus === true ||
            r?.offerCapping?.capping_status === true;

        if (!isOfferCapped) {
            return;
        }

        const queryParams = this.actRoute.snapshot.queryParams || {};
        if (r?.offerCapping?.redirectURI && r?.offerCapping.redirectURI !== "" &&
            r.offerCapping.alternateProsubId && r.offerCapping.alternateProsubId !== "") {
            const alternateProsubId = String(r.offerCapping.alternateProsubId);
            const currentProsubId = String(queryParams.prosub_id || queryParams.Prosub_ID || "");
            if (currentProsubId === alternateProsubId) {
                return;
            }

            localStorage.setItem("prosub_id", alternateProsubId);
            this.redirectWithFullPageLoad("/free-reg-step4c", {
                ...queryParams,
                prosub_id: alternateProsubId,
                TP1: TP1 ? TP1 : "",
                TP2: TP2 ? TP2 : "",
                TP3: TP3 ? TP3 : "",
                T_ID: T_ID ? T_ID : "",
                Other: Other ? Other : "",
                upsell_ID: upsell_ID ? upsell_ID : "",
            });
        } else if (r?.offerCapping?.redirectURI && r?.offerCapping?.redirectURI !== "" &&
            (!r.offerCapping.alternateProsubId || r.offerCapping.alternateProsubId === "")) {
            this.router.navigateByUrl("product-not-available");
        }
    }

    /** Only a committed server snapshot for the current account is a profile decision. */
    profileSnapshot$(): Observable<{ userId: string; data: Data } | null> {
        return combineLatest([
            this.authService.authReady$,
            fromEvent(window, "online").pipe(startWith(null)),
        ]).pipe(
            switchMap(([ready]) => ready ? new Observable<string | null>((subscriber) =>
                onAuthStateChanged(getAuth(),
                    (user) => subscriber.next(user && !user.isAnonymous ? user.uid : null),
                    (error) => subscriber.error(error),
                )).pipe(catchError(() => of(null))) : of(null)),
            switchMap((userId) => userId ? defer(() => new Observable<{ userId: string; data: Data } | null>((subscriber) => {
                subscriber.next(null);
                return onSnapshot(doc(this.firestore, `users/${userId}`), { includeMetadataChanges: true },
                    (snapshot) => {
                        const currentUser = getAuth().currentUser;
                        if (currentUser?.isAnonymous || currentUser?.uid !== userId ||
                            snapshot.metadata.fromCache !== false || snapshot.metadata.hasPendingWrites !== false || !snapshot.exists()) {
                            subscriber.next(null);
                            return;
                        }
                        subscriber.next({ userId, data: snapshot.data() });
                    },
                    (error) => subscriber.error(error),
                );
            })).pipe(catchError((error) => {
                this.authService.errorHandlerLogs("Post-payment profile lookup unavailable", error);
                return concat(of(null), throwError(() => error));
            }), retry({
                // Retry only a failed listener; healthy snapshots remain live.
                // Account/online changes and unsubscribe cancel this backoff.
                delay: (_error, attempt) => timer(Math.min(1000 * Math.pow(2, Math.min(attempt - 1, 5)), 30000)),
            })) : of(null)),
        );
    }

    /** Shared tri-state source for automatic recovery on the owning page. */
    profileCompletion$(): Observable<boolean | null> {
        return this.profileSnapshot$().pipe(
            map((snapshot) => profileCompletionState(snapshot?.data)),
            distinctUntilChanged(),
        );
    }

    async callInt2(ctx: CallInt2Context, options: { claimFallback?: boolean } = {}): Promise<void> {
        return this.withNavigationOwner(ctx, (owned) => this.continueAfterPayment(owned, options),
            options.claimFallback ? 10000 : 25000);
    }

    /** Use only after committed server profile completion has been confirmed. */
    async openConfirmedTickets(ctx: CallInt2Context): Promise<void> {
        return this.withNavigationOwner(ctx, (owned) => this.continueToTickets(owned), 10000);
    }

    private async withNavigationOwner(
        ctx: CallInt2Context,
        continueNavigation: (owned: CallInt2Context) => Promise<void>,
        timeoutMs: number,
    ): Promise<void> {
        let active = true;
        const ownedContext = { ...ctx, isCurrent: () => active && ctx.isCurrent?.() !== false };
        let deadline: ReturnType<typeof setTimeout> | undefined;
        try {
            // The caller owns the paid preparation view and automatic retry.
            // Late route/modal work cannot replace a newer navigation owner.
            await Promise.race([
                continueNavigation(ownedContext),
                new Promise<never>((_resolve, reject) => {
                    deadline = setTimeout(() => reject(new Error("Post-payment continuation timed out")), timeoutMs);
                }),
            ]);
        } finally {
            active = false;
            if (deadline !== undefined) clearTimeout(deadline);
        }
    }

    private async continueToTickets(ctx: CallInt2Context): Promise<void> {
        this.assertCurrentNavigation(ctx.isCurrent);
        try {
            const shown = await this.tryTicketsWithinDeadline(ctx._syndicateId ?? '', ctx.isCurrent);
            this.assertCurrentNavigation(ctx.isCurrent);
            if (shown) {
                this.clearPreparation();
                const paid = this.paymentService.confirmedPaidJourney;
                if (paid?.receiptId) this.paymentService.clearCheckoutReceiptResume(paid.receiptId);
                return;
            }
        } catch (error) {
            this.assertCurrentNavigation(ctx.isCurrent);
            this.authService.errorHandlerLogs("Post-payment tickets unavailable", error);
        }
        // Completed profiles must never be sent back to a profile form because
        // tickets or their lazy chunk are delayed. The paid view remains usable.
        return this.remainPreparing(ctx, "tickets_pending");
    }

    private async continueAfterPayment(ctx: CallInt2Context, options: { claimFallback?: boolean }): Promise<void> {
        const deadlineAt = Date.now() + PaymentService.POST_PAYMENT_NEXT_STEP_TIMEOUT_MS;
        try {
            this.assertCurrentNavigation(ctx.isCurrent);
            // Recovery for an already-confirmed purchase skips optional offers;
            // it only opens the existing profile/tickets result and never pays.
            const post_name = options.claimFallback
                ? "free-reg-complete-profile"
                : await this.paymentService.getOptionalPage("", deadlineAt, ctx.isCurrent);
            this.assertCurrentNavigation(ctx.isCurrent);
            const path = (post_name ?? '').split('?');

            if (post_name === "free-reg-step4c") {
                localStorage.setItem("ispostNameMatched", "true");
            } else {
                localStorage.removeItem("ispostNameMatched");
            }

            if (path[0] === "free-reg-complete-profile") {
                let complete: boolean | null = null;
                try {
                    complete = await firstValueFrom(this.profileCompletion$().pipe(
                        filter((value): value is boolean => value !== null),
                        take(1),
                        timeout(Math.max(1, deadlineAt - Date.now())),
                        defaultIfEmpty(null),
                    ));
                } catch (error) {
                    this.authService.errorHandlerLogs("Post-payment profile lookup unavailable", error);
                }
                this.assertCurrentNavigation(ctx.isCurrent);
                if (complete === null) return this.remainPreparing(ctx, "profile_pending");
                this.paymentService.freeRegJourneyStep = "free-reg-journey-4c";
                if (complete === true) {
                    await this.continueToTickets(ctx);
                    return;
                }
                // Only an explicit false may ask for genuinely missing details.
                this.clearPreparation();
                this.paymentService.freeRegJourneySuccessURL = path[0];
                ctx.onStateChange({ timerOn: false });
                const params = new URLSearchParams(path[1] ?? '');
                if (!params.has('prosub_id')) params.set('prosub_id', ctx.Prosub_id);
                params.set('TP1', ctx.TP1 || '');
                params.set('TP2', ctx.Tp2 || '');
                params.set('TP3', ctx.TP3 || '');
                params.set('T_ID', ctx.T_ID || '');
                this.assertCurrentNavigation(ctx.isCurrent);
                const navigated = await this.router.navigateByUrl(`${path[0]}?${params}`);
                this.assertCurrentNavigation(ctx.isCurrent);
                if (!navigated) throw new Error("Post-payment profile navigation was cancelled");
                return;
            }

            this.assertCurrentNavigation(ctx.isCurrent);
            ctx.onStateChange({ spinnerAnimation: false, addingCard: false, addNewCardInProgress: false });
            this.assertCurrentNavigation(ctx.isCurrent);
            localStorage.setItem("4cpage", "free-reg-step-4c");
            if (!path[0]) {
                const navigated = await this.router.navigate(['mainTabs/tabs/home']);
                this.assertCurrentNavigation(ctx.isCurrent);
                if (!navigated) throw new Error("Post-payment navigation was cancelled");
                return;
            }
            const currentSegments = this.actRoute.snapshot.url || [];
            if (currentSegments.some(segment => segment.path === "free-reg-step4c") && path[0] === "free-reg-step4c") {
                await this.waitForStoredCards(ctx.isCurrent);
                this.assertCurrentNavigation(ctx.isCurrent);
            }
            await this.freeReg4cNavigation(path, ctx);
        } catch (error) {
            this.authService.errorHandlerLogs('callInt2 navigation error:', error);
            // The page retains the confirmed-payment guard and can retry only
            // this continuation; never silently resolve or restart payment.
            throw error;
        }
    }

    private async tryTicketsWithinDeadline(syndicateId: string, isCurrent?: () => boolean): Promise<boolean> {
        let ticketAttemptActive = true;
        const controller = new AbortController();
        let deadline: ReturnType<typeof setTimeout> | undefined;
        try {
            // Return control to the paid preparation view if a lazy chunk or
            // Ionic modal promise stalls. Late modal creation loses ownership.
            return await Promise.race([
                this.fetchSyndicate(syndicateId, () => ticketAttemptActive && isCurrent?.() !== false, controller.signal),
                new Promise<boolean>((resolve) => {
                    deadline = setTimeout(() => {
                        ticketAttemptActive = false;
                        controller.abort();
                        resolve(false);
                    }, 3000);
                }),
            ]);
        } finally {
            ticketAttemptActive = false;
            if (deadline !== undefined) clearTimeout(deadline);
        }
    }

    private waitForStoredCards(isCurrent?: () => boolean): Promise<void> {
        this.assertCurrentNavigation(isCurrent);
        return new Promise<void>((resolve) => {
            if (isCurrent?.() !== false) this.loadingService.present();
            let finished = false;
            let snapshotStarted = false;
            let unsubscribeSnapshot: (() => void) | undefined;
            let uidSubscription: Subscription | undefined;
            const finish = () => {
                if (finished) return;
                finished = true;
                clearTimeout(listenerTimeout);
                uidSubscription?.unsubscribe();
                unsubscribeSnapshot?.();
                if (isCurrent?.() !== false) this.loadingService.dismiss();
                resolve();
            };
            // The original 15-second fallback now also bounds a missing UID.
            const listenerTimeout = setTimeout(finish, 15000);
            try {
                uidSubscription = (this.commonService.getUid("") || of(null)).pipe(take(1)).subscribe({
                    next: (uid) => {
                        if (isCurrent?.() === false) { finish(); return; }
                        if (!uid || uid === "null") {
                            this.authService.errorHandlerLogs("UserId not found.");
                            finish();
                            return;
                        }
                        snapshotStarted = true;
                        const cards = collection(this.firestore, `/users/${uid}/stored_card`);
                        unsubscribeSnapshot = onSnapshot(cards, {
                            next: (snapshot) => { if (isCurrent?.() === false || snapshot.docs.length > 0) finish(); },
                            error: () => finish(),
                        });
                        // Covers a synchronously delivered cached snapshot.
                        if (finished) unsubscribeSnapshot();
                    },
                    error: () => finish(),
                    complete: () => { if (!snapshotStarted) finish(); },
                });
                if (finished) uidSubscription.unsubscribe();
            } catch (error) {
                this.authService.errorHandlerLogs("Stored-card readiness unavailable", error);
                finish();
            }
        });
    }

    async freeReg4cNavigation(path: string[], ctx: CallInt2Context): Promise<void> {
        this.assertCurrentNavigation(ctx.isCurrent);
        const cardId = ctx.getCheckCardDetailId();
        const params = new URLSearchParams(path[1] ?? '');
        const prosub_id = params.get('prosub_id') ?? ctx.Prosub_id ?? '';
        const paidContext = this.paymentService.confirmedPaidJourney;
        const previousNextProsubId = paidContext?.nextProsubId;
        const marked = path[0] === "free-reg-step4b" &&
            this.paymentService.markConfirmedPaidJourneyNextOffer(ctx.Prosub_id, prosub_id);
        let navigated = false;
        try {
            navigated = await this.router.navigate([path[0]], {
                queryParams: {
                    prosub_id,
                    TP1: ctx.TP1 || 'null',
                    TP2: ctx.Tp2 || 'null',
                    TP3: ctx.TP3 || 'null',
                    T_ID: ctx.T_ID || '',
                    upsell_ID: ctx.upsellID || 'null',
                    purchase_ID: cardId?.id ?? 'null',
                },
            });
            this.assertCurrentNavigation(ctx.isCurrent);
            if (!navigated) throw new Error("Post-payment offer navigation was cancelled");
        } catch (error) {
            // Stamp before routing so the destination's entry hook can continue
            // the same paid purchase; cancelled routes cannot retain that claim.
            if (!navigated && marked && this.paymentService.confirmedPaidJourney === paidContext) {
                this.paymentService.markConfirmedPaidJourneyNextOffer(ctx.Prosub_id, previousNextProsubId);
            }
            throw error;
        }
    }

    async fetchSyndicate(syndicateId: string, isCurrent?: () => boolean, signal?: AbortSignal): Promise<boolean> {
        this.assertCurrentNavigation(isCurrent);
        const resolvedId =
            syndicateId || this.paymentService?.salesJournyPaymentDetails$?.value;
        if (resolvedId && resolvedId !== "0") {
            this.paymentService.salesJournyPaymentDetails$.next(resolvedId);
        }
        // Same generating UI as new-card. One-shot managed_tickets reads fail for
        // stored-card because the syndicate ID often arrives before products.
        return this.showTicket({}, true, isCurrent, signal);
    }

    handlePurchaseCapping(r: Data, userId: string) {
        let auth = getAuth();
        if (auth && auth?.currentUser && !auth?.currentUser?.isAnonymous) {
            const userDocRef = doc(this.firestore, `users/${userId}`);

            getDoc(userDocRef)
                .then((userRef) => {
                    if (userRef.exists()) {
                        const userData = userRef.data() as any;
                        if (!userData || !userData.minimal_required) {
                            this.router.navigate(["/free-reg-complete-profile"], {
                                queryParams: { sid: r?.purchaseCapping?.paymentCappingSyndicateId },
                            });
                        } else {
                            this.basePaymentService.fetchSyndicateAndShowTicket(r?.purchaseCapping?.paymentCappingSyndicateId).subscribe({
                                next: (syndicateInfo) => {
                                    this.showTicket(syndicateInfo);
                                },
                                error: (err) => {
                                    return err;
                                }
                            }
                            );
                        }
                    }
                })
                .catch((error) => {
                    this.authService.errorHandlerLogs("Error fetching user document:", error);
                });
        } else {
            this.router.navigate([r?.purchaseCapping?.paymentCappingURL]);
        }
    }
}
